import { useEffect, useRef } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Check,
  AlertCircle,
  Minus,
  Trash2,
} from 'lucide-react'
import { useContentCpStore, type LogEntry, type LogLevel } from '@/stores/useContentCpStore'
import { cn } from '@/lib/utils'

function formatElapsed(ms: number): string {
  const seconds = ms / 1000
  const minutes = Math.floor(seconds / 60)
  const secs = (seconds % 60).toFixed(3).padStart(6, '0')
  return minutes > 0 ? `${minutes}:${secs}` : `00:${secs}`
}

function LogIcon({ level, type }: { level: LogLevel; type: string }) {
  const iconClass = 'w-3 h-3 flex-shrink-0'

  // Check for specific event types
  if (type.endsWith('.complete') || type.endsWith('.done') || type === 'workflow.complete') {
    return <Check className={cn(iconClass, 'text-green-500')} strokeWidth={2} />
  }
  if (type.endsWith('.error') || level === 'error') {
    return <AlertCircle className={cn(iconClass, 'text-destructive')} strokeWidth={1.5} />
  }
  if (type.endsWith('.start') || type.includes('.processing')) {
    // Use a static icon for start/processing events in the log (they're historical, not live)
    return <Minus className={cn(iconClass, 'text-primary')} strokeWidth={1.5} />
  }

  // By level
  if (level === 'milestone') {
    return <Check className={cn(iconClass, 'text-green-500')} strokeWidth={2} />
  }
  if (level === 'progress') {
    return <Minus className={cn(iconClass, 'text-muted-foreground')} strokeWidth={1.5} />
  }
  if (level === 'detail') {
    return <Minus className={cn(iconClass, 'text-muted-foreground/50')} strokeWidth={1} />
  }
  // error or default
  return <Minus className={cn(iconClass, 'text-muted-foreground')} strokeWidth={1.5} />
}

function LogEntryRow({ entry, detailLevel }: { entry: LogEntry; detailLevel: LogLevel }) {
  // Filter by detail level
  const levelPriority: Record<LogLevel, number> = {
    milestone: 3,
    progress: 2,
    detail: 1,
    error: 4, // Always show errors
  }

  const entryPriority = levelPriority[entry.level]
  const filterPriority = levelPriority[detailLevel]

  if (entry.level !== 'error' && entryPriority < filterPriority) {
    return null
  }

  return (
    <div
      className={cn(
        'flex items-start gap-2 px-2 md:px-3 py-1 font-mono text-xs',
        entry.level === 'error' && 'bg-destructive/10',
        entry.level === 'milestone' && 'bg-green-500/5'
      )}
    >
      {/* Timestamp - hidden on mobile */}
      <span className="hidden sm:block text-muted-foreground/70 flex-shrink-0 w-16">
        [{formatElapsed(entry.elapsed_ms)}]
      </span>

      {/* Icon */}
      <LogIcon level={entry.level} type={entry.type} />

      {/* Message */}
      <span
        className={cn(
          'flex-1 break-words',
          entry.level === 'error' && 'text-destructive',
          entry.level === 'milestone' && 'text-green-600 dark:text-green-400',
          entry.level === 'detail' && 'text-muted-foreground'
        )}
      >
        {entry.message}
      </span>
    </div>
  )
}

interface WorkflowLogPanelProps {
  className?: string
}

export function WorkflowLogPanel({ className }: WorkflowLogPanelProps) {
  const { eventLog, batchState, toggleLogExpanded, clearLog, setLogDetailLevel } =
    useContentCpStore()
  const scrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new entries added
  useEffect(() => {
    if (eventLog.autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [eventLog.entries.length, eventLog.autoScroll])

  // Don't show if no entries and not processing
  if (eventLog.entries.length === 0 && !batchState.isProcessing) {
    return null
  }

  return (
    <div className={cn('border border-border rounded-lg overflow-hidden bg-background flex flex-col', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border flex-shrink-0">
        <button
          onClick={toggleLogExpanded}
          className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground/80"
        >
          {eventLog.expanded ? (
            <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
          ) : (
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          )}
          <span className="hidden sm:inline">Processing Log</span>
          <span className="sm:hidden">Log</span>
          <span className="text-xs text-muted-foreground ml-1">
            ({eventLog.entries.length})
          </span>
        </button>

        <div className="flex items-center gap-1 sm:gap-2">
          {/* Detail level selector */}
          <select
            value={eventLog.detailLevel}
            onChange={(e) => setLogDetailLevel(e.target.value as LogLevel)}
            className="text-xs bg-muted/50 border border-border rounded px-1.5 sm:px-2 py-1 text-foreground"
          >
            <option value="milestone">Milestones</option>
            <option value="progress">Progress</option>
            <option value="detail">All</option>
          </select>

          {/* Clear button */}
          <button
            onClick={clearLog}
            className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
            title="Clear log"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Log entries - fills available space */}
      {eventLog.expanded && (
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto bg-muted/10 min-h-0"
        >
          {eventLog.entries.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              Waiting for processing to start...
            </div>
          ) : (
            <div className="py-1">
              {eventLog.entries.map((entry) => (
                <LogEntryRow
                  key={entry.id}
                  entry={entry}
                  detailLevel={eventLog.detailLevel}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
